/** @format */

import React from "react";
import {
	Box,
	Container,
	Typography,
	Grid,
	Card,
	CardContent,
	Button,
	styled,
	Paper,
} from "@mui/material";
import PeopleIcon from "@mui/icons-material/People";
import EventIcon from "@mui/icons-material/Event";
import PaymentIcon from "@mui/icons-material/Payment";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import {
	ArrowForward,
	GroupAdd,
	Schedule,
	Payments,
	CheckCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { colors } from "../../utility/types/constant";
import { CTAButtonsWrapper, PrimaryCTAButton } from "../banner-one/styles";
import { SEO } from "../allComponents/AllComponents";

const HeroSection = styled(Box)(({ theme }) => ({
	background: colors.primary,
	color: "#fff",
	padding: "80px 0",
	textAlign: "center",
	fontFamily: "'Montserrat', sans-serif",
}));

const FeatureCard = styled(Card)(({ theme }) => ({
	height: "100%",
	display: "flex",
	flexDirection: "column",
	transition: "transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out",
	"&:hover": {
		transform: "translateY(-8px)",
		boxShadow: "0 12px 24px rgba(0,0,0,0.15)",
	},
}));

const IconWrapper = styled(Box)(({ theme }) => ({
	width: "64px",
	height: "64px",
	borderRadius: "50%",
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 16px",
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepCard = styled(Paper)(({ theme }) => ({
	backgroundColor: "#ffffff",
	borderRadius: "20px",
	padding: "40px 30px",
	boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
	border: "2px solid rgba(0,0,0,0.05)",
	transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
	textAlign: "center",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		right: 0,
		height: "4px",
		background: colors.mainSecondary,
		transform: "scaleX(0)",
		transition: "transform 0.4s ease",
	},
	"&:hover": {
		transform: "translateY(-10px)",
		boxShadow: "0 20px 40px rgba(30, 193, 149, 0.2)",
		borderColor: colors.mainSecondary,
		"&::before": {
			transform: "scaleX(1)",
		},
	},
	[theme.breakpoints.down("md")]: {
		padding: "30px 20px",
	},
}));

const StepNumber = styled(Typography)(({ theme }) => ({
	position: "absolute",
	top: "10px",
	right: "15px",
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(48px, 6vw, 72px)",
	fontWeight: 900,
	color: "rgba(30, 193, 149, 0.08)",
	lineHeight: 1,
}));

const StepIconWrapper = styled(Box)(({ theme }) => ({
	width: "80px",
	height: "80px",
	borderRadius: "50%",
	backgroundColor: colors.mainSecondary,
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	margin: "0 auto 24px auto",
	color: "#ffffff",
	transition: "all 0.3s ease",
	boxShadow: "0 8px 20px rgba(30, 193, 149, 0.3)",
}));

const StepTitle = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "clamp(1.25rem, 2vw, 1.5rem)",
	fontWeight: 700,
	color: colors.primary,
	marginBottom: "12px",
}));

const StepDescription = styled(Typography)(({ theme }) => ({
	fontFamily: "'Montserrat', sans-serif",
	fontSize: "0.9375rem",
	fontWeight: 400,
	color: "#6c757d",
	lineHeight: 1.7,
}));

const ConnectorLine = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	[theme.breakpoints.down("md")]: {
		display: "none",
	},
}));

const AnimatedArrow = styled(Box)(({ theme }) => ({
	color: colors.mainSecondary,
	animation: "slideRight 2s ease-in-out infinite",
	"@keyframes slideRight": {
		"0%, 100%": {
			transform: "translateX(0)",
		},
		"50%": {
			transform: "translateX(10px)",
		},
	},
}));

const CTAButton = styled(Button)(({ theme }) => ({
	backgroundColor: colors.mainSecondary,
	color: "#fff",
	padding: "12px 32px",
	fontSize: "16px",
	fontWeight: 600,
	borderRadius: "8px",
	textTransform: "none",
	"&:hover": {
		backgroundColor: colors.mainSecondary,
		transform: "scale(1.05)",
	},
}));

const GroupContributions: React.FC = () => {
	const navigate = useNavigate();
	const handleJoinClick = () => {
		navigate("/circle/sign-up");
	};

	const features = [
		{
			icon: <PeopleIcon sx={{ fontSize: 40 }} />,
			title: "Collective Funding",
			description:
				"Pool resources with your group for weddings, parties, gifts, or any shared expense. Everyone contributes their share.",
		},
		{
			icon: <EventIcon sx={{ fontSize: 40 }} />,
			title: "Event-Based Contributions",
			description:
				"Perfect for birthdays, graduations, baby showers, or any celebration. Make group gifting easy and organized.",
		},
		{
			icon: <PaymentIcon sx={{ fontSize: 40 }} />,
			title: "Flexible Payment Options",
			description:
				"Set up one-time or recurring contributions. Members can pay at their own pace or all at once.",
		},
		{
			icon: <VerifiedUserIcon sx={{ fontSize: 40 }} />,
			title: "Transparent Tracking",
			description:
				"See who has contributed and how much in real-time. Full transparency for all group members.",
		},
		{
			icon: <NotificationsActiveIcon sx={{ fontSize: 40 }} />,
			title: "Smart Reminders",
			description:
				"Automated reminders keep everyone on track. No more awkward follow-ups or missed deadlines.",
		},
		{
			icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
			title: "Secure Fund Management",
			description:
				"Your money is held securely until the goal is reached. Complete peace of mind for all contributors.",
		},
	];

	const howItWorksSteps = [
		{
			step: "01",
			icon: <GroupAdd sx={{ fontSize: 40 }} />,
			title: "Create a Group",
			description:
				"Set up a contribution group for your event or goal. Invite members via email or share a link.",
		},
		{
			step: "02",
			icon: <Schedule sx={{ fontSize: 40 }} />,
			title: "Set the Goal",
			description:
				"Define the total amount needed and contribution deadline. Set individual or equal contribution amounts.",
		},
		{
			step: "03",
			icon: <Payments sx={{ fontSize: 40 }} />,
			title: "Collect Contributions",
			description:
				"Members contribute securely through the platform. Track progress in real-time as funds come in.",
		},
		{
			step: "04",
			icon: <CheckCircle sx={{ fontSize: 40 }} />,
			title: "Release Funds",
			description:
				"Once the goal is met, funds are released to the organizer. Use it for your intended purpose.",
		},
	];

	const benefits = [
		"Eliminate awkward money conversations with friends and family",
		"No more chasing people down for contributions",
		"Perfect for weddings, parties, group gifts, and shared expenses",
		"Complete transparency - everyone sees the progress",
		"Secure payment processing with buyer protection",
		"Automated reminders and notifications",
	];

	return (
		<>
			<SEO
				title="Group Contributions"
				description="Pool resources with your group for events, gifts, and shared expenses. FundsyFi makes collective funding easy with flexible payment options and secure transactions."
				keywords="group contributions, collective funding, event funding, group gifting, shared expenses, pool money, crowdfunding friends"
				url="/products/group-contributions"
			/>
			<Box sx={{ bgcolor: "#f5f5f5" }}>
				{/* Hero Section */}
				<HeroSection>
					<Container maxWidth="lg">
						<Typography
							variant="h2"
							sx={{
								fontWeight: 700,
								mb: 2,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Group Contributions
						</Typography>
						<Typography
							variant="h5"
							sx={{
								mb: 4,
								opacity: 0.9,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Simplify Group Gifting & Shared Expenses
						</Typography>
						<Typography
							variant="h6"
							sx={{
								maxWidth: "800px",
								margin: "0 auto",
								mb: 4,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Make collecting money from groups effortless. Whether it's for a
							wedding gift, party, or any shared expense, organize contributions
							seamlessly with complete transparency.
						</Typography>
						<CTAButtonsWrapper
							data-aos="fade-up"
							data-aos-duration="1000"
							data-aos-delay="400">
							<PrimaryCTAButton
								variant="contained"
								size="large"
								onClick={handleJoinClick}>
								Start a Collection
							</PrimaryCTAButton>
						</CTAButtonsWrapper>
					</Container>
				</HeroSection>

				{/* What is Group Contributions */}
				<Container maxWidth="lg" sx={{ py: 8 }}>
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						What are Group Contributions?
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "900px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Group Contributions is a modern solution for collecting money from
						multiple people for a shared purpose. Instead of dealing with cash,
						Venmo requests, or spreadsheets, organize everything in one secure
						platform. Perfect for weddings, birthdays, office gifts, team
						events, or any situation where a group needs to pool money together.
					</Typography>

					{/* Features Grid */}
					<Grid container spacing={4} sx={{ mb: 8 }}>
						{features.map((feature, index) => (
							<Grid item xs={12} sm={6} md={4} key={index}>
								<FeatureCard>
									<CardContent>
										<IconWrapper>{feature.icon}</IconWrapper>
										<Typography
											variant="h6"
											sx={{ fontWeight: 600, mb: 2, color: "#333" }}>
											{feature.title}
										</Typography>
										<Typography
											variant="body2"
											sx={{ color: "#666", lineHeight: 1.7 }}>
											{feature.description}
										</Typography>
									</CardContent>
								</FeatureCard>
							</Grid>
						))}
					</Grid>
				</Container>

				{/* How It Works */}
				<Box sx={{ bgcolor: "#fff", py: 8 }}>
					<Container maxWidth="lg">
						<Typography
							variant="h3"
							align="center"
							sx={{
								fontWeight: 800,
								mb: 2,
								color: colors.primary,
								fontFamily: "'Montserrat', sans-serif",
								fontSize: "clamp(2rem, 4vw, 3rem)",
								letterSpacing: "-0.02em",
							}}>
							How It Works
						</Typography>
						<Typography
							variant="body1"
							align="center"
							sx={{
								maxWidth: "800px",
								margin: "0 auto 6rem",
								fontSize: "clamp(1rem, 2vw, 1.25rem)",
								color: "#6c757d",
								fontFamily: "'Montserrat', sans-serif",
								lineHeight: 1.7,
							}}>
							Collect group contributions in 4 simple steps and make shared
							expenses stress-free
						</Typography>

						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: {
									xs: "1fr",
									md: "1fr auto 1fr auto 1fr auto 1fr",
								},
								gap: { xs: "40px", md: "20px" },
								alignItems: "center",
							}}>
							{howItWorksSteps.map((item, index) => (
								<React.Fragment key={index}>
									<StepCard elevation={2}>
										<StepNumber>{item.step}</StepNumber>
										<StepIconWrapper>{item.icon}</StepIconWrapper>
										<StepTitle>{item.title}</StepTitle>
										<StepDescription>{item.description}</StepDescription>
									</StepCard>

									{index < howItWorksSteps.length - 1 && (
										<ConnectorLine>
											<AnimatedArrow>
												<ArrowForward sx={{ fontSize: 32 }} />
											</AnimatedArrow>
										</ConnectorLine>
									)}
								</React.Fragment>
							))}
						</Box>
					</Container>
				</Box>

				{/* Benefits Section */}
				<Container maxWidth="lg" sx={{ py: 8 }}>
					<Typography
						variant="h3"
						align="center"
						sx={{
							fontWeight: 800,
							mb: 2,
							color: colors.primary,
							fontFamily: "'Montserrat', sans-serif",
							fontSize: "clamp(2rem, 4vw, 3rem)",
							letterSpacing: "-0.02em",
						}}>
						Why Choose Group Contributions?
					</Typography>
					<Typography
						variant="body1"
						align="center"
						sx={{
							maxWidth: "800px",
							margin: "0 auto 6rem",
							fontSize: "clamp(1rem, 2vw, 1.25rem)",
							color: "#6c757d",
							fontFamily: "'Montserrat', sans-serif",
							lineHeight: 1.7,
						}}>
						Say goodbye to the hassle of collecting money from groups
					</Typography>

					<Grid
						container
						spacing={3}
						sx={{ maxWidth: "900px", margin: "0 auto" }}>
						{benefits.map((benefit, index) => (
							<Grid item xs={12} sm={6} key={index}>
								<Box sx={{ display: "flex", alignItems: "flex-start" }}>
									<Box
										sx={{
											width: "24px",
											height: "24px",
											borderRadius: "50%",
											backgroundColor: colors.mainSecondary,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: "#fff",
											fontSize: "14px",
											fontWeight: 600,
											mr: 2,
											flexShrink: 0,
											mt: 0.5,
										}}>
										✓
									</Box>
									<Typography
										variant="body1"
										sx={{
											color: "#6c757d",
											lineHeight: 1.7,
											fontFamily: "'Montserrat', sans-serif",
										}}>
										{benefit}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>
				</Container>

				{/* CTA Section */}
				<Box
					sx={{
						backgroundColor: colors.primary,
						color: "#fff",
						py: 8,
					}}>
					<Container maxWidth="md" sx={{ textAlign: "center" }}>
						<Typography
							variant="h3"
							sx={{
								fontWeight: 700,
								mb: 2,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Ready to Simplify Group Payments?
						</Typography>
						<Typography
							variant="h6"
							sx={{
								mb: 4,
								opacity: 0.9,
								fontFamily: "'Montserrat', sans-serif",
							}}>
							Join thousands using FundsyFi to organize group contributions with
							ease
						</Typography>
						<Box
							sx={{
								display: "flex",
								gap: 2,
								justifyContent: "center",
								flexWrap: "wrap",
							}}>
							<CTAButton
								onClick={() => navigate("/circle/sign-up")}
								sx={{
									bgcolor: "#fff",
									color: colors.primary,
									"&:hover": {
										bgcolor: "#fff",
									},
								}}>
								Start a Collection
							</CTAButton>
							<CTAButton
								onClick={() => navigate("/circle/sign-in")}
								sx={{
									bgcolor: "transparent",
									border: "2px solid #fff",
									"&:hover": {
										bgcolor: "rgba(255,255,255,0.1)",
									},
								}}>
								Sign In
							</CTAButton>
						</Box>
					</Container>
				</Box>
			</Box>
		</>
	);
};

export default GroupContributions;
